import * as React from "react";
import { Controller } from "react-hook-form";
import type {
  Control,
  FieldPath,
  FieldValues,
  RegisterOptions,
} from "react-hook-form";
import { Input } from "@/shared/ui/components/ui/input";
import { Label } from "@/shared/ui/components/ui/label";
import { cn } from "@/shared/lib/utils";

export interface NumberLabelInputBaseProps
  extends Omit<
    React.InputHTMLAttributes<HTMLInputElement>,
    "type" | "value" | "onChange"
  > {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: React.ReactNode;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  rightLabel?: string;
  maxLength?: number;
  onErrorClear?: () => void;
}

const NumberLabelInputBase = React.forwardRef<
  HTMLInputElement,
  NumberLabelInputBaseProps
>(
  (
    {
      className,
      label,
      hint,
      error,
      leftIcon,
      value,
      onChange,
      disabled,
      id,
      rightLabel,
      onErrorClear,
      maxLength,
      onKeyDown: onKeyDownProp,
      ...props
    },
    ref
  ) => {
    const inputId = id || React.useId();
    const hasError = Boolean(error);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      if (hasError && onErrorClear) {
        onErrorClear();
      }
      if (maxLength && e.target.value.length > maxLength) {
        e.preventDefault();
        return;
      }
      onChange?.(e);
    };

    // Prevent scroll from changing the number value
    const handleWheel = (e: React.WheelEvent<HTMLInputElement>) => {
      e.currentTarget.blur();
      e.stopPropagation();
    };

    // Prevent typing 'e' and 'E' characters 
    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'e' || e.key === 'E') {
        e.preventDefault();
        return;
      }
      onKeyDownProp?.(e);
    };

    return (
      <div className={cn("w-full gap-3", className)}>
        {label && (
          <Label
            htmlFor={inputId}
            className={cn(
              "mb-3 block text-sm font-medium text-grey-900",
              disabled && "text-grey-200",
              hasError && "text-error-700"
            )}
          >
            {label}
          </Label>
        )}

        <div
          className={cn(
            "flex rounded-lg border overflow-hidden",
            hasError ? "border-error-700" : "border-grey-100",
            disabled && "border-grey-100 bg-grey-50"
          )}
        >
          <div className="relative flex-1 group">
            {leftIcon && (
              <div
                className={cn(
                  "absolute start-5 top-1/2 -translate-y-1/2 transition-colors text-grey-200 z-10",
                  "group-focus-within:text-grey-900",
                  disabled && "text-grey-200",
                  hasError && "text-error-700 group-focus-within:text-error-700"
                )}
              >
                {leftIcon}
              </div>
            )}

            <Input
              id={inputId}
              ref={ref}
              type="number"
              value={value ?? ""}
              onChange={handleChange}
              onWheel={handleWheel}
              onKeyDown={handleKeyDown}
              maxLength={maxLength}
              disabled={disabled}
              className={cn(
                "px-5 py-4 bg-white text-sm font-normal shadow-none",
                "focus-visible:ring-0",
                "disabled:bg-grey-50 border-grey-100",
                "placeholder:text-grey-200",
                leftIcon && "ps-12",
                "rounded-none rounded-s-lg border-0 border-e-0",
                "hover:border-brand-green hover:text-grey-200",
                "focus-visible:border-brand-green focus-visible:text-grey-900",
                // Hide number input arrows
                "[appearance:textfield]",
                "[&::-webkit-outer-spin-button]:appearance-none",
                "[&::-webkit-inner-spin-button]:appearance-none",
                disabled &&
                "bg-grey-50 border-grey-100 text-grey-200 cursor-not-allowed hover:text-grey-200 hover:border-grey-100",
                hasError &&
                "bg-error-bg text-error-700 placeholder:text-error-700 hover:text-error-700 focus-visible:text-error-700"
              )}
              {...props}
            />
          </div>

          {rightLabel && (
            <div
              className={cn(
                "flex items-center justify-center px-4 border-s bg-grey-50",
                hasError ? "border-error-700" : "border-grey-100",
                disabled && "border-grey-100"
              )}
            >
              <span
                className={cn(
                  "text-sm font-medium whitespace-nowrap",
                  hasError
                    ? "text-error-700"
                    : disabled
                      ? "text-grey-300"
                      : "text-grey-400"
                )}
              >
                {rightLabel}
              </span>
            </div>
          )}
        </div>

        {(hint || error) && (
          <p
            className={cn(
              "mt-3 text-xs font-medium text-grey-300",
              hasError && "text-error-700"
            )}
          >
            {error || hint}
          </p>
        )}
      </div>
    );
  }
);

NumberLabelInputBase.displayName = "NumberLabelInputBase";

export interface NumberLabelInputProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
> extends Omit<NumberLabelInputBaseProps, "name" | "value" | "onChange" | "error"> {
  name?: TName;
  control?: Control<TFieldValues>;
  rules?: Omit<
    RegisterOptions<TFieldValues, TName>,
    "valueAsNumber" | "valueAsDate" | "setValueAs" | "disabled"
  >;
  value?: string | number;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  error?: string;
  maxLength?: number;
}

function NumberLabelInput<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>
>(
  props: NumberLabelInputProps<TFieldValues, TName> & {
    ref?: React.Ref<HTMLInputElement>;
  }
) {
  const { name, control, rules, error: errorProp, ...restProps } = props;

  if (control && name) {
    return (
      <Controller<TFieldValues, TName>
        name={name}
        control={control as any}
        rules={rules as any}
        render={({ field, fieldState }) => (
          <NumberLabelInputBase
            {...restProps}
            {...field}
            value={field.value ?? ""}
            error={fieldState.error?.message || errorProp}
          />
        )}
      />
    );
  }

  return <NumberLabelInputBase {...restProps} error={errorProp} />;
}

const NumberLabelInputWithRef = React.forwardRef<
  HTMLInputElement,
  NumberLabelInputProps<FieldValues, FieldPath<FieldValues>>
>((props, ref) => {
  const { name, control, rules, error: errorProp, ...restProps } = props;
  if (control && name) {
    return (
      <Controller
        name={name as any}
        control={control as any}
        rules={rules as any}
        render={({ field, fieldState }) => (
          <NumberLabelInputBase
            {...restProps}
            {...field}
            value={field.value ?? ""}
            error={fieldState.error?.message || errorProp}
            ref={ref}
          />
        )}
      />
    );
  }

  return <NumberLabelInputBase {...restProps} error={errorProp} ref={ref} />;
});

NumberLabelInputWithRef.displayName = "NumberLabelInput";

export { NumberLabelInputWithRef as NumberLabelInput };
